import type { Invoice } from "@/features/invoices/components/InvoiceTable"
import { parseSeats } from "./seat-defaults"
import type {
  CreateInvoiceForm,
  CreateInvoiceIn,
  DepositLeg,
  DepositPaymentOut,
  DepositPlan,
  DepositPlanOut,
  DiscountType,
  InstallmentCountOption,
  InstallmentPlan,
  InstallmentPlanOut,
  InstallmentProgress,
  InstallmentProgressOut,
  InvoiceDetail,
  InvoiceDetailOut,
  InvoiceLineItem,
  InvoiceLineItemOut,
  InvoiceOut,
  LineItemForm,
  LineItemIn,
  PaymentMode,
  UpdateInvoiceIn,
} from "./types"

export function toShortId(uuid: string): string {
  return uuid.replace(/-/g, "").slice(0, 8).toUpperCase()
}

export function formatInvoiceNumber(dto: {
  id: string
  created_at: string
  stripe_account: string
}): string {
  const year = new Date(dto.created_at).getFullYear()
  return `${year}-${dto.stripe_account}-${toShortId(dto.id)}`
}

export function buildHubspotDealUrl(dealId: string): string | undefined {
  const portal = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID
  if (!portal || !dealId) return undefined
  return `https://app.hubspot.com/contacts/${portal}/deal/${encodeURIComponent(dealId)}`
}

function parseAmount(raw: string): number {
  const n = Number.parseFloat(raw)
  return Number.isFinite(n) ? n : 0
}

function parseBareDateLocal(raw: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  if (!match) return new Date(raw)
  return new Date(
    Number.parseInt(match[1], 10),
    Number.parseInt(match[2], 10) - 1,
    Number.parseInt(match[3], 10),
  )
}

export function toFrontendInvoice(dto: InvoiceOut): Invoice {
  const fullName = `${dto.first_name} ${dto.last_name}`.trim()
  return {
    id: dto.id,
    invoiceNumber: formatInvoiceNumber(dto),
    status: dto.status,
    customer: {
      name: fullName,
      email: dto.email,
      company: "",
    },
    course: dto.course_name,
    expirationDate: parseBareDateLocal(dto.expiration_date),
    amount: parseAmount(dto.amount),
    // The discount-inclusive price the customer pays. Fall back to gross
    // `amount` for older fixtures that predate the field.
    netAmount: parseAmount(dto.net_amount ?? dto.amount),
    currency: dto.currency.toUpperCase(),
    // Default to "full" so older list fixtures (no computed field) read
    // as ordinary rows rather than crashing the indicator branch.
    paymentMode: dto.payment_mode ?? "full",
    createdAt: new Date(dto.created_at),
    updatedAt: new Date(dto.updated_at),
    createdById: dto.created_by.id,
    hubspotDealUrl: buildHubspotDealUrl(dto.hubspot_deal_id),
  }
}

function formDiscount(form: CreateInvoiceForm) {
  if (form.discountMode === "none") {
    return null
  }
  return { type: form.discountMode, value: form.discountValue }
}

export function discountEquals(
  a: { type: string; value: string } | null,
  b: { type: string; value: string } | null,
): boolean {
  if (a === null && b === null) return true
  if (a === null || b === null) return false
  return a.type === b.type && a.value === b.value
}

export function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i])
}

/**
 * Map the form's repeatable course rows to the wire ``line_items[]``.
 * Each row's ``price`` is in its base currency; the server converts it
 * into the invoice ``currency``. ``base_currency`` rides only when the SR
 * set a per-row override (course + cohort both lacked a currency).
 * A row with a picked cohort sends ``cohort_id``; a cohortless row (the
 * course has no cohorts, so the picker falls back to a manual date input)
 * sends ``course_start_date`` instead — exactly one per row.
 */
/** A blank course row for the create/edit forms' "Add course" control. */
export function emptyLineItem(): LineItemForm {
  return {
    courseId: "",
    cohortId: null,
    price: "",
    baseCurrency: "",
    courseName: "",
    courseNameDefault: "",
    startDate: "",
  }
}

/** Sum the per-row base-currency prices into a combined-subtotal preview
 *  string (``""`` when nothing positive yet). Best-effort: the server
 *  re-converts + sums authoritatively; this is the form's running total. */
export function combinedLinePrice(
  lineItems: readonly LineItemForm[],
): string {
  const total = lineItems.reduce((sum, li) => {
    const n = Number.parseFloat(li.price)
    return Number.isFinite(n) ? sum + n : sum
  }, 0)
  return total > 0 ? String(total) : ""
}

/**
 * Whether a row's display name is an SR override: a non-empty name that
 * differs from the Planna default (both compared trimmed). The single
 * predicate the wire mapper and the ``useCourseNameBinding`` hook (Reset
 * affordance + initial latch) share, so "is this row overridden?" can never
 * disagree between the UI and what actually gets sent. Backend twin:
 * ``invoices.service.normalize_course_name_override``.
 */
export function isCourseNameOverride(
  name: string,
  defaultName: string,
): boolean {
  const trimmed = name.trim()
  return trimmed !== "" && trimmed !== defaultName.trim()
}

export function toLineItemsPayload(
  lineItems: readonly LineItemForm[],
): LineItemIn[] {
  return lineItems.map((row) => {
    const line: LineItemIn = {
      course_id: row.courseId,
      price: row.price,
    }
    // Exactly one start-date source per line (the backend enforces the same
    // XOR): a selected cohort, else the SR's manual start date for a
    // cohortless course.
    if (row.cohortId !== null) {
      line.cohort_id = row.cohortId
    } else {
      line.course_start_date = row.startDate
    }
    if (row.baseCurrency) {
      line.base_currency = row.baseCurrency
    }
    // Send the display name only when it's an override. An un-edited row
    // (name == the default) omits it, which the backend reads as "no
    // override"; that also expresses "reset to default". The schema
    // guarantees it's never blank.
    if (isCourseNameOverride(row.courseName, row.courseNameDefault)) {
      line.course_name = row.courseName.trim()
    }
    return line
  })
}

/**
 * Structural equality of two form line-item rows. ``startDate`` is ignored
 * for cohorted rows (the server derives ``course_start_date`` from the
 * cohort), but IS compared for cohortless rows (``cohortId === null``),
 * where the SR's manual start date is the meaningful, sent value — so
 * nudging it is detected as a change and re-sent on PATCH.
 */
export function lineItemFormEquals(
  a: LineItemForm,
  b: LineItemForm,
): boolean {
  return (
    a.courseId === b.courseId &&
    a.cohortId === b.cohortId &&
    a.price === b.price &&
    a.baseCurrency === b.baseCurrency &&
    // A display-name rename (or reset) is a meaningful change to re-send, so
    // compare the effective name — trimmed, matching the payload's shape.
    a.courseName.trim() === b.courseName.trim() &&
    (a.cohortId !== null || a.startDate === b.startDate)
  )
}

export function lineItemsFormEqual(
  a: readonly LineItemForm[],
  b: readonly LineItemForm[],
): boolean {
  return a.length === b.length && a.every((row, i) => lineItemFormEquals(row, b[i]))
}

export function toUpdateInvoicePayload(
  form: CreateInvoiceForm,
  baseline: CreateInvoiceForm,
): UpdateInvoiceIn {
  const patch: UpdateInvoiceIn = {}
  const formFirstName = form.firstName.trim()
  const baselineFirstName = baseline.firstName.trim()
  if (formFirstName !== baselineFirstName) patch.first_name = formFirstName

  const formLastName = form.lastName.trim()
  const baselineLastName = baseline.lastName.trim()
  if (formLastName !== baselineLastName) patch.last_name = formLastName

  const formEmail = form.email.trim().toLowerCase()
  const baselineEmail = baseline.email.trim().toLowerCase()
  if (formEmail !== baselineEmail) patch.email = formEmail

  // Phone clears by sending "" (the backend stores empty-string, not
  // null), so unlike memo we emit the trimmed value verbatim.
  const formPhone = form.phone.trim()
  const baselinePhone = baseline.phone.trim()
  if (formPhone !== baselinePhone) patch.phone = formPhone

  const formDealId = form.dealId.trim()
  const baselineDealId = baseline.dealId.trim()
  if (formDealId !== baselineDealId) patch.deal_id = formDealId

  const currencyChanged = form.currency !== baseline.currency
  if (currencyChanged) patch.currency = form.currency

  // A currency change MUST carry line_items (the backend 422s a
  // currency-only patch without them — it needs the per-line base prices
  // to re-convert). Otherwise, send line_items only when the rows
  // actually changed (a wholesale replace re-resolves + recomputes
  // aggregates). A row is only serializable once it has a start-date source
  // — a picked cohort OR a manual start date (cohortless course); a
  // mid-edit incomplete row leaves the persisted courses untouched.
  const lineItemsChanged = !lineItemsFormEqual(form.lineItems, baseline.lineItems)
  if (
    (lineItemsChanged || currencyChanged) &&
    form.lineItems.length > 0 &&
    form.lineItems.every(
      (row) => row.cohortId !== null || Boolean(row.startDate),
    )
  ) {
    patch.line_items = toLineItemsPayload(form.lineItems)
  }

  // Seats: compare the parsed whole-int values so a "2" vs " 2 " text
  // difference isn't treated as a change. Emit only when the count moved.
  if (parseSeats(form.seats) !== parseSeats(baseline.seats)) {
    patch.seats = parseSeats(form.seats)
  }

  // Title: emit the verbatim value (incl. "", which clears it back to the
  // composed default) only when it changed against the baseline.
  if (form.title !== baseline.title) {
    patch.title = form.title
  }

  if (form.paymentDeadline !== baseline.paymentDeadline) {
    patch.payment_deadline = form.paymentDeadline
  }
  const formMemo = form.memo.trim()
  const baselineMemo = baseline.memo.trim()
  if (formMemo !== baselineMemo) patch.memo = formMemo === "" ? null : formMemo

  const nextDiscount = formDiscount(form)
  const prevDiscount = formDiscount(baseline)
  if (!discountEquals(nextDiscount, prevDiscount)) patch.discount = nextDiscount

  // Named discounts: order matters (it is the cascade order), so compare
  // the ordered id lists. Emit the full replacement set (incl. []) when
  // they differ; omit otherwise so the backend leaves attachments alone.
  if (!arraysEqual(form.namedDiscountIds, baseline.namedDiscountIds)) {
    patch.named_discount_ids = [...form.namedDiscountIds]
  }

  // Emit payment_mode only when the effective plan changed, mirroring
  // the backend's legal PATCH shapes: count/cadence accompany it only
  // for installments (and the diff compares the full effective plan so a
  // count/cadence tweak within installments re-emits all three). For
  // "deposit"/"full" only payment_mode is sent.
  const nextPlan = formPaymentPlan(form)
  const prevPlan = formPaymentPlan(baseline)
  if (!paymentPlanEquals(nextPlan, prevPlan)) {
    patch.payment_mode = nextPlan.mode
    if (nextPlan.mode === "installments") {
      patch.installment_count = nextPlan.count
      patch.installment_cadence = nextPlan.cadence
    }
  }

  // Backup-card requirement (installments only). Emit when the invoice is
  // (or becomes) installments AND the rep changed the checkbox (or just
  // switched into installments — a fresh plan is seeded from the global
  // default, so the rep's choice must ride along). Never emitted for a
  // non-installments result, which the backend 422s (design §3).
  if (
    form.paymentMode === "installments" &&
    (form.requireBackupCard !== baseline.requireBackupCard ||
      baseline.paymentMode !== "installments")
  ) {
    patch.require_backup_card = form.requireBackupCard
  }

  // Balance deadline (deposit only). Emit when the invoice is (or becomes) a
  // deposit invoice AND the rep changed the balance deadline (or just
  // switched into deposit mode). "" ⇒ null (reset to the invoice expiration
  // date, the backend's explicit-null sentinel). Never emitted for a
  // non-deposit result, which the backend 422s (deposits-deadline-edit §3.2).
  if (
    form.paymentMode === "deposit" &&
    (form.depositBalanceDeadline !== baseline.depositBalanceDeadline ||
      baseline.paymentMode !== "deposit")
  ) {
    patch.deposit_balance_deadline =
      form.depositBalanceDeadline === ""
        ? null
        : form.depositBalanceDeadline
  }

  return patch
}

interface EffectivePaymentPlan {
  mode: PaymentMode
  count: number | null
  cadence: CreateInvoiceForm["installmentCadence"] | null
}

function formPaymentPlan(form: CreateInvoiceForm): EffectivePaymentPlan {
  if (form.paymentMode === "installments") {
    return {
      mode: "installments",
      count: Number.parseInt(form.installmentCount, 10),
      cadence: form.installmentCadence,
    }
  }
  // count/cadence are irrelevant outside installments, so they never
  // contribute to the diff for "full"/"deposit". The create-only
  // ``on_call`` channel maps to a "full" invoice (on-call-payment design).
  const mode: PaymentMode =
    form.paymentMode === "on_call" ? "full" : form.paymentMode
  return { mode, count: null, cadence: null }
}

function paymentPlanEquals(
  a: EffectivePaymentPlan,
  b: EffectivePaymentPlan,
): boolean {
  return a.mode === b.mode && a.count === b.count && a.cadence === b.cadence
}

export function toCreateInvoicePayload(
  form: CreateInvoiceForm,
): CreateInvoiceIn {
  const memo = form.memo.trim()
  // Throws when any row's cohort is unpicked (cohort_id ge=1 is
  // required); the picker is its source of truth.
  const lineItems = toLineItemsPayload(form.lineItems)
  const payload: CreateInvoiceIn = {
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim(),
    deal_id: form.dealId.trim(),
    line_items: lineItems,
    seats: parseSeats(form.seats),
    // The composed (or SR-edited) title; blank lets the backend compose
    // the default. Always sent.
    title: form.title,
    currency: form.currency,
    discount:
      form.discountMode === "none"
        ? null
        : { type: form.discountMode, value: form.discountValue },
    payment_deadline: form.paymentDeadline,
    memo: memo === "" ? null : memo,
    // Always sent. count/cadence are included only for installments, per
    // the backend's "omit unless installments" cross-field rule. The
    // create-only ``on_call`` channel maps to a one-time ``full`` invoice
    // (it is charged on the call, not a stored payment mode) — the backend
    // never sees ``on_call`` (on-call-payment design §1).
    payment_mode: form.paymentMode === "on_call" ? "full" : form.paymentMode,
  }
  if (form.namedDiscountIds.length > 0) {
    payload.named_discount_ids = [...form.namedDiscountIds]
  }
  if (form.paymentMode === "installments") {
    payload.installment_count = Number.parseInt(form.installmentCount, 10)
    payload.installment_cadence = form.installmentCadence
    // Backup-card requirement is installments-only (the backend 422s it for
    // any other mode). Always sent for installments so the rep's choice —
    // including a deliberate deviation from the global default — is honoured.
    payload.require_backup_card = form.requireBackupCard
  }
  // Balance deadline is meaningful only for deposit mode; sent only when the
  // rep set a concrete override (empty ⇒ let the backend default it to the
  // invoice expiration date).
  if (form.paymentMode === "deposit" && form.depositBalanceDeadline !== "") {
    payload.deposit_balance_deadline = form.depositBalanceDeadline
  }
  return payload
}

function toFrontendInstallmentPlan(
  plan: InstallmentPlanOut | null | undefined,
): InstallmentPlan | null {
  if (!plan) return null
  return {
    id: plan.id,
    numInstallments: plan.num_installments,
    cadence: plan.cadence,
    totalAmount: parseAmount(plan.total_amount),
    totalAmountRaw: plan.total_amount,
    currency: plan.currency,
    startDate: parseBareDateLocal(plan.start_date),
    // Older fixtures/back-compat: default to today's mandatory behaviour
    // when the field is absent.
    requireBackupCard: plan.require_backup_card ?? true,
    pausedAt: plan.paused_at ? new Date(plan.paused_at) : null,
    resumeOn: plan.resume_on ? parseBareDateLocal(plan.resume_on) : null,
    schedule: plan.schedule.map((row) => ({
      sequence: row.sequence,
      scheduleId: row.schedule_id,
      dueDate: parseBareDateLocal(row.due_date),
      amount: parseAmount(row.amount),
      amountRaw: row.amount,
      currency: row.currency,
      status: row.status,
      declineCategory: row.decline_category,
      lastDeclineCode: row.last_decline_code,
      lastErrorCode: row.last_error_code,
      fraudFlag: row.fraud_flag,
      retryCount: row.retry_count,
      backupRetryCount: row.backup_retry_count,
      lastPaymentIntentId: row.last_payment_intent_id,
      lastFailurePaymentIntentId: row.last_failure_pi_id,
      nextAttemptAfter: row.next_attempt_after
        ? parseBareDateLocal(row.next_attempt_after)
        : null,
      taxMinor: row.tax_minor ?? null,
    })),
  }
}

export function toFrontendInstallmentProgress(
  progress: InstallmentProgressOut | null | undefined,
): InstallmentProgress | null {
  if (!progress) return null
  return {
    paidCount: progress.paid_count,
    totalCount: progress.total_count,
    nextDueDate: progress.next_due_date
      ? parseBareDateLocal(progress.next_due_date)
      : null,
    nextDueAmount:
      progress.next_due_amount !== null
        ? parseAmount(progress.next_due_amount)
        : null,
    nextDueAmountRaw: progress.next_due_amount,
    nextDueSequence: progress.next_due_sequence,
    currentScheduleStatus: progress.current_schedule_status,
    lastDeclineCategory: progress.last_decline_category,
    lastDeclineCode: progress.last_decline_code,
  }
}

function toFrontendDepositLeg(leg: DepositPaymentOut): DepositLeg {
  return {
    stage: leg.stage,
    amount: parseAmount(leg.amount),
    amountRaw: leg.amount,
    currency: leg.currency,
    status: leg.status,
    dueDate: parseBareDateLocal(leg.due_date),
    paidAt: leg.paid_at ? new Date(leg.paid_at) : null,
    payable: leg.payable,
    emailSentAt: leg.email_sent_at ? new Date(leg.email_sent_at) : null,
    paymentMethodType: leg.payment_method_type,
    paymentMethodBrand: leg.payment_method_brand,
    paymentMethodLast4: leg.payment_method_last4,
    taxMinor: leg.tax_minor ?? null,
  }
}

export function toFrontendDepositPlan(
  plan: DepositPlanOut | null | undefined,
): DepositPlan | null {
  if (!plan) return null
  return {
    id: plan.id,
    totalAmount: parseAmount(plan.total_amount),
    totalAmountRaw: plan.total_amount,
    currency: plan.currency,
    deposit: toFrontendDepositLeg(plan.deposit),
    balance: toFrontendDepositLeg(plan.balance),
  }
}

/**
 * Derive the frontend payment mode. Prefers the backend's computed
 * ``payment_mode`` and falls back to the plan FKs for fixture
 * back-compat (older fixtures may omit the computed field).
 */
function derivePaymentMode(dto: InvoiceDetailOut): PaymentMode {
  if (dto.payment_mode) return dto.payment_mode
  if (dto.deposit_plan) return "deposit"
  if (dto.installment_plan) return "installments"
  return "full"
}

function toFrontendLineItem(line: InvoiceLineItemOut): InvoiceLineItem {
  return {
    position: line.position,
    courseId: line.course_id,
    courseCode: line.course_code,
    courseName: line.course_name,
    // Fall back to the effective name for fixtures that predate the field.
    courseNameDefault: line.course_name_default ?? line.course_name,
    cohortId: line.cohort_id,
    courseStartDate: parseBareDateLocal(line.course_start_date),
    // Per-seat unit (seeds the row Price). Fall back to the per-course
    // total for legacy fixtures that predate the split.
    unitPrice: line.unit_price ?? line.amount,
    amount: line.amount,
    baseCurrency: line.base_currency,
    marketName: line.market_name,
    instructorName: line.instructor_name,
    instructorPhotoUrl: line.instructor_photo_url,
    fullPrice: line.full_price,
    tierIndex: line.tier_index,
  }
}

/**
 * Build the frontend line-item list. Prefers the backend ``line_items``
 * (already ordered by position); for legacy fixtures without it, falls
 * back to a single synthesized row from the top-level primary-mirror
 * fields so the edit form still re-hydrates one course row.
 */
function toFrontendLineItems(dto: InvoiceDetailOut): InvoiceLineItem[] {
  if (dto.line_items && dto.line_items.length > 0) {
    return dto.line_items.map(toFrontendLineItem)
  }
  return [
    {
      position: 0,
      courseId: dto.course_id,
      courseCode: dto.course_code,
      courseName: dto.course_name,
      // Legacy single-course fallback: the top-level mirror is already the
      // effective name and carries no separate default, so use it as the
      // reset baseline too.
      courseNameDefault: dto.course_name,
      // The detail payload's primary mirror carries no cohort id; the
      // picker re-emits it on edit.
      cohortId: null,
      courseStartDate: parseBareDateLocal(dto.course_start_date),
      // Legacy single-course fallback: the top-level ``amount`` is the
      // per-course value here (single course = unit for 1 seat), so it
      // doubles as the unit-price seed.
      unitPrice: dto.amount,
      amount: dto.amount,
      baseCurrency: "",
      marketName: dto.market_name,
      instructorName: "",
      instructorPhotoUrl: "",
      fullPrice: null,
      tierIndex: null,
    },
  ]
}

export function toFrontendInvoiceDetail(dto: InvoiceDetailOut): InvoiceDetail {
  const fullName = `${dto.first_name} ${dto.last_name}`.trim()
  const reminderDate = dto.reminder_date
    ? parseBareDateLocal(dto.reminder_date)
    : null
  const discount =
    dto.discount_type === "percent" || dto.discount_type === "fixed"
      ? {
          type: dto.discount_type as DiscountType,
          value: dto.discount_value ?? "",
        }
      : null
  return {
    id: dto.id,
    invoiceNumber: formatInvoiceNumber(dto),
    status: dto.status,
    customer: {
      name: fullName,
      email: dto.email,
    },
    firstName: dto.first_name,
    lastName: dto.last_name,
    email: dto.email,
    phone: dto.phone,
    dealId: dto.hubspot_deal_id,
    courseId: dto.course_id,
    course: dto.course_name,
    courseCode: dto.course_code,
    amount: parseAmount(dto.amount),
    // Seat count for the whole order; default to 1 for fixture back-compat.
    seats: dto.seats ?? 1,
    // SR-edited title verbatim ("" when never edited / absent fixture).
    title: dto.title ?? "",
    price: dto.amount,
    currency: dto.currency.toUpperCase(),
    stripeAccount: dto.stripe_account,
    discount,
    memo: dto.memo,
    createdAt: new Date(dto.created_at),
    updatedAt: new Date(dto.updated_at),
    deadline: reminderDate,
    reminderDate,
    courseStartDate: parseBareDateLocal(dto.course_start_date),
    expirationDate: parseBareDateLocal(dto.expiration_date),
    lineItems: toFrontendLineItems(dto),
    hubspotDealUrl: buildHubspotDealUrl(dto.hubspot_deal_id),
    createdBy: dto.created_by,
    installmentPlan: toFrontendInstallmentPlan(dto.installment_plan),
    installmentProgress: toFrontendInstallmentProgress(dto.installment_progress),
    paymentMode: derivePaymentMode(dto),
    depositPlan: toFrontendDepositPlan(dto.deposit_plan),
    marketName: dto.market_name,
    // Already ordered by position from the backend; map to catalog ids
    // for the edit-form multi-select.
    namedDiscountIds: (dto.named_discounts ?? []).map(
      (link) => link.named_discount_id,
    ),
    // id → snapshot name, so the edit form can label and remove an
    // attached discount even after it drops out of the active catalog.
    namedDiscountMeta: Object.fromEntries(
      (dto.named_discounts ?? []).map((link) => [
        link.named_discount_id,
        link.name,
      ]),
    ),
    // Server-derived attribution, display-only. Default "" so older
    // fixtures that predate the columns still produce a populated block.
    marketing: {
      utmSource: dto.utm_source ?? "",
      utmMedium: dto.utm_medium ?? "",
      utmContent: dto.utm_content ?? "",
      landingSlug: dto.landing_slug ?? "",
    },
  }
}

/**
 * Project a domain line item to a form row. Uses the line's per-seat
 * issue-currency ``unitPrice`` as the Price field — the field is the
 * per-seat unit and the backend derives each course's total as
 * ``round(unit_price × seats)``, so seeding the total (``amount``) would
 * re-multiply on save. The cohort id is taken from the line when present
 * (so an edit can re-send it identity-safe).
 *
 * ``baseCurrency`` is seeded with the invoice's **issue** currency, not
 * the line's stored pre-conversion ``base_currency``: ``price`` already
 * holds the converted issue-currency amount, so re-submitting an
 * unchanged row must convert ``issue -> issue`` (identity). Feeding the
 * original base back would re-convert the already-converted price and
 * silently re-price the course on every save. (Mirrors the extension's
 * ``PrefillDraftLineItem.base_currency = invoice.currency``.)
 */
function lineItemToFormRow(
  line: InvoiceLineItem,
  currency: string,
): LineItemForm {
  return {
    courseId: line.courseId,
    cohortId: line.cohortId,
    price: line.unitPrice,
    baseCurrency: currency,
    // Seed the display-name input from the effective name; stash the Planna
    // default as the reset baseline (equal when un-overridden).
    courseName: line.courseName,
    courseNameDefault: line.courseNameDefault,
    startDate: formatDateLocalIso(line.courseStartDate),
  }
}

export function invoiceDetailToFormValues(
  detail: InvoiceDetail,
): CreateInvoiceForm {
  // The issued currency is any payable code now (not just usd/gbp), so
  // seed the picker with it verbatim (lower-cased to the form shape).
  const currency = detail.currency.toLowerCase()
  const detailLineItems = detail.lineItems ?? []
  const lineItems: LineItemForm[] =
    detailLineItems.length > 0
      ? detailLineItems.map((line) => lineItemToFormRow(line, currency))
      : [
          {
            courseId: detail.courseId,
            // Detail payload doesn't carry the cohort id; the picker
            // re-emits it.
            cohortId: null,
            price: detail.price,
            // ``price`` is already in the issue currency, so the base is
            // the issue currency too (identity re-convert on re-submit).
            baseCurrency: currency,
            // Legacy single-course draft: the top-level mirror is the
            // effective name and its own default baseline.
            courseName: detail.course,
            courseNameDefault: detail.course,
            startDate: formatDateLocalIso(detail.courseStartDate),
          },
        ]
  return {
    firstName: detail.firstName,
    lastName: detail.lastName,
    email: detail.email,
    phone: detail.phone,
    dealId: detail.dealId,
    lineItems,
    // Seed the seats input from the stored count (string form value).
    seats: String(detail.seats ?? 1),
    // Seed the title input from the stored value; "" when never edited.
    // The composed-title hook starts frozen (seeded) so a stored label is
    // never re-composed.
    title: detail.title ?? "",
    currency,
    discountMode: detail.discount ? detail.discount.type : "none",
    discountValue: detail.discount ? detail.discount.value : "",
    namedDiscountIds: [...(detail.namedDiscountIds ?? [])],
    paymentDeadline: formatDateLocalIso(detail.reminderDate),
    memo: detail.memo,
    // Seed the payment-mode picker from the saved plan so reopening a
    // draft restores the mode (and, for installments, the persisted
    // count/cadence). Fall back to the form defaults when there's no plan.
    paymentMode: detail.paymentMode,
    installmentCount: detail.installmentPlan
      ? (String(detail.installmentPlan.numInstallments) as InstallmentCountOption)
      : "4",
    installmentCadence: detail.installmentPlan
      ? detail.installmentPlan.cadence
      : "monthly",
    // Restore the per-plan backup-card choice from the saved plan so
    // reopening an installments draft shows the persisted value; default to
    // the mandatory behaviour (true) for a non-installments draft (the
    // checkbox is hidden then).
    requireBackupCard: detail.installmentPlan
      ? detail.installmentPlan.requireBackupCard
      : true,
    // Restore the balance deadline from the deposit plan's balance leg so
    // reopening a deposit draft shows the persisted (possibly overridden)
    // date; "" for non-deposit drafts (deposits-deadline-edit §5.2).
    depositBalanceDeadline: detail.depositPlan
      ? formatDateLocalIso(detail.depositPlan.balance.dueDate)
      : "",
  }
}

export function formatDateLocalIso(date: Date | null): string {
  if (!date) return ""
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}
